import { io, Socket } from 'socket.io-client'
import { WS_API_URL } from 'config'

type WsHandler<TMessage = any> = (message: TMessage) => void

/**
 * Room-based Socket.IO client. The server only delivers events to sockets
 * that joined the related room (userId, chatId, 'app-versions', ...), and
 * events arrive named after the action ('new_message', 'stream', ...).
 */
class SocketServices {
  socket: Socket
  // room -> number of active subscribers, so overlapping components can
  // share a room and we leave it only when the last one unsubscribes
  private rooms = new Map<string, number>()

  constructor(url?: string) {
    console.debug('SocketServices constructor', url)
    if (!url) {
      throw new Error('missing websocket url')
    }
    this.socket = io(url, {
      transports: ['websocket'],
    })

    this.socket.on('connect', this.onConnect)
    this.socket.on('disconnect', this.onDisconnect)
    this.socket.on('connect_error', this.onConnectError)
  }

  // Rooms live on the server-side connection: they must be re-joined after
  // every reconnect or the client silently stops receiving events.
  private onConnect = () => {
    console.debug('SocketServices connected')
    for (const room of this.rooms.keys()) {
      this.socket.emit('join', room)
    }
  }

  private onDisconnect = (reason: Socket.DisconnectReason, description?: any) => {
    console.debug('SocketServices disconnected', { reason, description })
  }

  private onConnectError = (err: Error) => {
    console.debug('SocketServices connection error', { err })
  }

  join(room: string) {
    const count = this.rooms.get(room) ?? 0
    this.rooms.set(room, count + 1)
    // when not connected yet, onConnect will flush the join
    if (count === 0 && this.socket.connected) {
      this.socket.emit('join', room)
    }
  }

  leave(room: string) {
    const count = this.rooms.get(room) ?? 0
    if (count <= 1) {
      this.rooms.delete(room)
      if (this.socket.connected) {
        this.socket.emit('leave', room)
      }
    } else {
      this.rooms.set(room, count - 1)
    }
  }

  on<TMessage = any>(event: string, handler: WsHandler<TMessage>) {
    this.socket.on(event, handler)
  }

  // handler is required: removing by event name only would also detach
  // the listeners registered by other components on the same action
  off<TMessage = any>(event: string, handler: WsHandler<TMessage>) {
    this.socket.off(event, handler)
  }
}

export const socketSingleton = new SocketServices(WS_API_URL)
